import { nextPowerOf2, hasConsolation } from "@/utils/league-bracket";

// Brackets label slots R<round>.<slot>, except the championship which is "FINAL".
function roundOf (slotId) {
  if (slotId === "FINAL") return null;
  const match = /^R(\d+)\./.exec(slotId || "");
  return match ? Number(match[1]) : null;
}

// A team that loses in elimination round `round` finishes at 2^(rounds - round) + 1
// (semifinal losers tie at 3, quarterfinal losers at 5, ...).
function placeForLoss (round, rounds) {
  return 2 ** (rounds - round) + 1;
}

function compareRows (a, b) {
  const pts = b.points - a.points;
  if (pts !== 0) return pts;
  const w = b.wins - a.wins;
  if (w !== 0) return w;
  const diff = b.goalDiff - a.goalDiff;
  if (diff !== 0) return diff;
  return a.ordinalSum - b.ordinalSum;
}

// The final league place for a squad that entered the bracket, from how deep it got
// eliminated. The champion is 1, the runner-up 2, and everyone else ties at the place of
// the round in which they lost (3 for semifinalists, 5 for quarterfinalists, ...). When the
// league has a third-place consolation match its two semifinal losers are not guaranteed 3rd
// while the consolation is undecided — they hold 3rd or 4th, so null keeps them unrevealed.
function bracketPlace (row, { bracketSquads, racesBySlot, rounds, consolation }) {
  const id = row.squad.id;
  const finalRace = racesBySlot.get("FINAL");
  const finalIds = bracketSquads.FINAL || [];
  if (finalRace && finalRace.decided && finalIds.includes(id)) {
    return finalRace.winningSquadId === id ? 1 : 2;
  }
  const thirdIds = bracketSquads.THIRD;
  const thirdRace = racesBySlot.get("THIRD");
  if (thirdIds && thirdIds.includes(id)) {
    if (thirdRace && thirdRace.decided) {
      return thirdRace.winningSquadId === id ? 3 : 4;
    }
    return null;
  }
  for (const [slotId, ids] of Object.entries(bracketSquads)) {
    if (!ids[0] || !ids[1]) continue;
    if (!ids.includes(id)) continue;
    const race = racesBySlot.get(slotId);
    if (race && race.decided && race.winningSquadId !== id) {
      const round = roundOf(slotId);
      if (round == null) continue;
      if (consolation && round === rounds - 1) return null;
      return placeForLoss(round, rounds);
    }
  }
  return null;
}

// Final season standings for a league with a bracket, computed progressively. Once the round
// robin has decided seeding (bracketSquads resolves participants), bracket finishers take
// 1/2/3/5... by how deep they went (or 1/2/3/4 with a decided third-place consolation), and
// non-bracket squads follow in round-robin order. A squad's place only gets revealed when it is
// guaranteed: squads still in the bracket are flagged `revealed: false` so the view masks them,
// holding the positions that would fall to their strongest remaining outcome. Eliminated
// (non-semifinal) and non-bracket squads are `revealed: true`. Bracket games do not count
// toward W/L, Pts or Goals — only round-robin results do.
export function finalStandings ({ rankedSquads, bracketSize, bracketSquads = {}, races = [], finished }) {
  if (bracketSize < 2) return rankedSquads;

  const racesBySlot = new Map(races.map(r => [r.bracketSlot, r]));
  const participantIds = new Set();
  for (const ids of Object.values(bracketSquads)) {
    for (const id of ids) if (id) participantIds.add(id);
  }
  if (participantIds.size < 2) return rankedSquads;

  const rows = rankedSquads;
  const consolation = hasConsolation(Math.min(bracketSize, rows.length));

  const fullSize = nextPowerOf2(bracketSize);
  const rounds = Math.log2(fullSize);

  const bracketRows = [];
  const tailRows = [];
  for (const row of rows) {
    if (participantIds.has(row.squad.id)) bracketRows.push(row);
    else tailRows.push(row);
  }

  const unresolvedRows = [];
  const placedRows = [];
  for (const row of bracketRows) {
    const place = bracketPlace(row, { bracketSquads, racesBySlot, rounds, consolation });
    if (place == null) {
      unresolvedRows.push({ ...row, place: null, revealed: false });
    } else {
      placedRows.push({ ...row, place, revealed: true });
    }
  }

  placedRows.sort((a, b) => (a.place - b.place) || compareRows(a, b));
  unresolvedRows.sort(compareRows);

  // Lay resolved participants at their exact places and let unresolved ones fill the remaining
  // positions, strongest round-robin seed first, so a squads' name and place are only shown when
  // that position is guaranteed.
  const placedByPlace = new Map();
  for (const row of placedRows) {
    placedByPlace.set(row.place, (placedByPlace.get(row.place) || []).concat(row));
  }
  const maxPlace = placedByPlace.size > 0 ? Math.max(...placedByPlace.keys()) : 0;
  const ordered = [];
  let unresolvedIndex = 0;
  const span = Math.max(maxPlace, placedRows.length + unresolvedRows.length);
  for (let place = 1; place <= span; place++) {
    const atPlace = placedByPlace.get(place);
    if (atPlace) {
      ordered.push(...atPlace);
      continue;
    }
    if (unresolvedIndex < unresolvedRows.length) {
      ordered.push({ ...unresolvedRows[unresolvedIndex], place });
      unresolvedIndex++;
    }
  }

  // Non-bracket squads keep matching the round-robin tiebreaks and are renumbered after all
  // bracket participants; a tie keeps the previous place and the next squad jumps by one.
  tailRows.sort(compareRows);
  let tailIndex = 0;
  const tailStart = participantIds.size + 1;
  for (const row of tailRows) {
    if (tailIndex > 0 && compareRows(tailRows[tailIndex - 1], row) === 0) {
      row.place = tailRows[tailIndex - 1].place;
    } else {
      row.place = tailStart + tailIndex;
    }
    row.revealed = true;
    tailIndex++;
  }

  return [...ordered, ...tailRows];
}
